/**
 * Driving duration: client Distance Matrix first (same key as Places), then estimate fallback.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import type { DrivingDurationInput } from './calendarEventGroupPlan';
import { estimateDrivingSecondsFromCoords, formatMatrixLocation } from './drivingDurationEstimate';
import { getClientDrivingDuration } from './clientDistanceMatrix';
import { geocodeAddress } from './geocoding';

const regionFunctions = () => getFunctions(undefined, 'us-central1');

const cache = new Map<string, { durationSeconds: number; distanceMeters?: number; estimated?: boolean }>();

function cacheKey(input: DrivingDurationInput): string {
  const o = formatMatrixLocation(input.origin, input.originLat, input.originLng);
  const d = formatMatrixLocation(input.destination, input.destinationLat, input.destinationLng);
  return `${o}|${d}`;
}

async function withGeocodedCoords(input: DrivingDurationInput): Promise<DrivingDurationInput> {
  let { originLat, originLng, destinationLat, destinationLng } = input;

  if ((originLat == null || originLng == null) && input.origin.trim()) {
    const coords = await geocodeAddress(input.origin);
    if (coords) {
      originLat = coords.lat;
      originLng = coords.lng;
    }
  }

  if ((destinationLat == null || destinationLng == null) && input.destination.trim()) {
    const coords = await geocodeAddress(input.destination);
    if (coords) {
      destinationLat = coords.lat;
      destinationLng = coords.lng;
    }
  }

  return {
    ...input,
    originLat,
    originLng,
    destinationLat,
    destinationLng,
  };
}

function estimateFromCoords(input: DrivingDurationInput) {
  if (
    input.originLat == null ||
    input.originLng == null ||
    input.destinationLat == null ||
    input.destinationLng == null
  ) {
    return null;
  }
  return {
    durationSeconds: estimateDrivingSecondsFromCoords(
      input.originLat,
      input.originLng,
      input.destinationLat,
      input.destinationLng,
    ),
    estimated: true as const,
  };
}

export async function getDrivingDuration(
  executiveUid: string,
  input: DrivingDurationInput,
): Promise<{ durationSeconds: number; distanceMeters?: number; estimated?: boolean }> {
  const key = cacheKey(input);
  const hit = cache.get(key);
  if (hit) return hit;

  const resolved = await withGeocodedCoords(input);
  const errors: string[] = [];

  try {
    const client = await getClientDrivingDuration(resolved);
    const result = { ...client, estimated: false as const };
    cache.set(key, result);
    return result;
  } catch (err) {
    errors.push(err instanceof Error ? err.message : 'client matrix failed');
  }

  try {
    const res = await httpsCallable(regionFunctions(), 'executiveCalendarGetDrivingDuration')({
      executiveUid,
      origin: resolved.origin,
      destination: resolved.destination,
      originLat: resolved.originLat,
      originLng: resolved.originLng,
      destinationLat: resolved.destinationLat,
      destinationLng: resolved.destinationLng,
    });
    const data = res.data as { durationSeconds: number; distanceMeters?: number };
    const result = { ...data, estimated: false as const };
    cache.set(key, result);
    return result;
  } catch (err) {
    errors.push(err instanceof Error ? err.message : 'server matrix failed');
  }

  const estimate = estimateFromCoords(resolved);
  if (estimate) {
    cache.set(key, estimate);
    return estimate;
  }

  throw new Error(errors.join('; ') || 'Could not compute driving time');
}

export function clearDrivingDurationCache(): void {
  cache.clear();
}
